import { createFileRoute } from '@tanstack/react-router';
import { AdminModerationView } from '@/components/admin/AdminModerationView';
import type { AdminReportQueueInput } from '@/lib/queries/admin';

type TabKey = 'all' | 'action_needed' | 'action_taken' | 'hidden';

// Facet params (targetKind / reason / confidence) are deliberately NOT
// consumed here. TanStack Router merges the raw search over validateSearch's
// result, so validation at the route cannot strip an invalid value; the view
// owns facet parsing via useFilterParams + zod (see AdminModerationView).
interface ModerationSearch {
  readonly status?: TabKey;
  readonly q?: string;
  readonly cursor?: string;
}

const ALLOWED_TABS: ReadonlyArray<TabKey> = [
  'all',
  'action_needed',
  'action_taken',
  'hidden',
];

function statusFilterFor(
  tab: TabKey,
): AdminReportQueueInput['status'] | undefined {
  if (tab === 'action_taken') return 'actioned';
  if (tab === 'action_needed') return 'open';
  if (tab === 'hidden') return 'hidden';
  return undefined;
}

export const Route = createFileRoute('/_authed/admin/moderation/')({
  validateSearch: (raw: Record<string, unknown>): ModerationSearch => {
    const status =
      typeof raw.status === 'string' &&
      (ALLOWED_TABS as ReadonlyArray<string>).includes(raw.status)
        ? (raw.status as TabKey)
        : undefined;
    const q = typeof raw.q === 'string' && raw.q.length > 0 ? raw.q : undefined;
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    return {
      ...(status !== undefined ? { status } : {}),
      ...(q !== undefined ? { q } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    };
  },
  component: ModerationQueuePage,
});

function ModerationQueuePage() {
  const search = Route.useSearch();
  const active: TabKey = search.status ?? 'action_needed';

  const filterStatus = statusFilterFor(active);
  const queueInput: AdminReportQueueInput = {
    pageSize: 50,
    ...(filterStatus ? { status: filterStatus } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
  };

  return (
    <AdminModerationView
      queueInput={queueInput}
      active={active}
      searchQuery={search.q}
      cursor={search.cursor}
    />
  );
}
